/** ***********
*
* <user-right.ts v1.0>
*
* Copyright (c) 2023 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*  NG6C740 [thomas.pereira.external@airbus.com]
*
* All rights reserved.
**************/

import { FamilyRight, ParentFamilyRight } from './family';
import { Scope } from './profile';
import { User } from './user';

export enum RoleLevelEnum {
    basic = 'basic',
    manager = 'manager',
    administrator = 'administrator',
    operator = 'operator',
    reader = 'reader',
}

export const ROLES_I18N = Object.freeze([
    { value: RoleLevelEnum.basic, text: RoleLevelEnum.basic },
    { value: RoleLevelEnum.reader, text: RoleLevelEnum.reader },
    { value: RoleLevelEnum.operator, text: RoleLevelEnum.operator },
    { value: RoleLevelEnum.manager, text: RoleLevelEnum.manager },
    { value: RoleLevelEnum.administrator, text: RoleLevelEnum.administrator },
]);

export type RoleLevel = keyof typeof RoleLevelEnum;


export interface AskRightDialogData {
    managers: Array<User>;
    parentFamilyName?: string;
    familyName?: string;
    level?: string;
}
export interface AskRightDialogResult {
    action: 'confirm' | 'cancel';
    manager?: User;
    level?: RoleLevel;
}

export interface ModifyRoleDialogData {
    family: FamilyRight;
    parentFamily: ParentFamilyRight;
    requestTarget: FamilyRight | ParentFamilyRight;
    showBasicRole?: boolean;
}
export interface SpecificRoleDialogData {
    userId: string;
    email: string;
    id: string;
    option: Option;
}

export interface SpecificRoleDialogResult {
    action: 'update' | 'cancel';
    option?: Option;
}

export interface ModifyRoleDialogResult {
    action: 'update' | 'cancel';
    level?: RoleLevel;
}

export interface FamilyUsersConfirmationDialogData {
    role: RoleLevel;
}

export interface FamilyUsersConfirmationDialogResult {
    action: 'confirm' | 'cancel';
}

export interface Option {
    mobile: boolean;
    adminMobile: boolean;
    planningManager: boolean;
    trackerPlanningManager: boolean;
    adminPosition: boolean;
    administrator: boolean;
    adminZone: boolean;
    adminGroup: boolean;
    outdatedHorizonManagement: boolean;
}

export interface RemoveRoleDialogData {
    parentFamily: ParentFamilyRight;
    family?: FamilyRight;
    userId: string;
}

export type RemoveRoleDialogResult = 'cancel' | 'confirm';

export interface DecodedToken {
    ADAMUniqueKey: string;
    UniqueKey: string; // email address
    aud: string; // "https://v3.airbus.com/FedBroker"
    client_id: string; // "CLI_2H66_WAY-V"
    corp_id: string; // ""
    email: string; // email address
    exp: number; // 1609427585
    family_name: string;
    given_name: string;
    iss: string; // "https://ssobroker-val.airbus.com:10443"
    name: string; // "NAME, Firstname (SPARTE AIRBUS THIRD PARTY)"
    preferred_username: string; // NG ID
    scope: Array<string>; // ["SCO_2H66_API_WAY-V"]
    sub: string; // email address
    type: string; // "U2M"
}

export interface UpdateRightAPIPayload {
    familyId?: string | undefined; // undefined if for parent
    parentFamilyId: string;
    level: RoleLevel;
}

export interface UpdateRightActionPayload {
    userId: string;
    parentFamilyId: string;
    parentFamilyLevel?: RoleLevel;
    familyId?: string;
    familyLevel?: RoleLevel;
    expanded?: boolean;
}

export interface SuccessFamilyRightPayload {
    parentFamilyId: string;
    userId: string;
    familiesRights: FamilyRight[];
}

export interface FamilyManagers {
    managers: User[];
    familyId: string;
}

export interface RightRequestAPIBody {
    managerId: string;
    level: RoleLevel;
    parentFamilyId: string;
    familyId: string;
    scope: string;
}
export interface RightRequest {
    id: string;
    date: string;
    requesterId: string;
    requesterUserId: string;
    requesterMail: string;
    requesterFirstname?: string;
    requesterLastname?: string;
    familyName?: string;
    familyId?: string;
    parentFamilyName: string;
    parentFamilyId: string;
    level: RoleLevel;
    managerId: string;
    managerUserId: string;
    managerMail?: string;
    managerFirstname?: string;
    managerLastname?: string;
    isLoading?: boolean;
    groupId?: string;
    groupName?: string;
    note?: string
}

export interface UserRoleLevel {
    data: string;
    email: string;
    famRightLevel: RoleLevel;
    userId?: string;
    fromGroupId?: string;
}

export interface GetUsersPerFamily {
    userId: string;
    mail: string;
    famRightLevel: RoleLevel;
    fromGroupId?: string;
}
export interface Group {
  id?: string;
  name: string;
  isOwner?: boolean;
  isManager?: boolean;
  owner?: string;
  managers? : string[];
  description?: string;
  mobile?: boolean;
  quantityOfUsers?: number;
  quantityOfFamilies?: number;
  users?: User[];
  families?: GroupFamily[];
  requested?: boolean;
}

export interface GroupFamily {
  id: string;
  level: RoleLevelEnum;
  scope: Scope;
  name?: string;
}

export interface RequestAGroup {
    id: string;
    groupName?: string;
    managerEmail?: string[];
    note?: string;
}

export interface GroupRequestExists {
    isRequestExists: boolean;
    data: RequestAGroup|[];
}

export interface SendGroupRequest {
    groupId: string;
    note?: string;
}

export type GroupIdName = (Partial<Group> & Required<Pick<Group, 'id' | 'name'>>);

export interface RemoveRolesFromParentFamilyOrFamily {
    id: string;
    name: string;
    scope?: string;
    parentFamilyId?: string;
    description?: string;
}


export interface DeleteMyRightsPayload {
    isParentFamily: boolean;
    deleteMyRight: RemoveRolesFromParentFamilyOrFamily;
}

export type FamilyRightCache = RemoveRolesFromParentFamilyOrFamily & { level: RoleLevelEnum };
